'use client';

import { useEffect, useRef } from 'react';

/**
 * Field reveals animate `height: 0 → auto` over 200ms, so a scroll fired on the
 * same tick measures the pre-reveal height and stops short. Wait for the reveal
 * to land before measuring.
 */
const REVEAL_SETTLE_MS = 250;

/**
 * Scrolls a region to its bottom the first time `isArmed` turns true — but only
 * when the region actually overflows.
 *
 * Completing the last mandatory field on a step reveals the optional fields
 * below it (phone, display language), which on a short viewport pushes them and
 * the now-enabled CTA under the fold with no cue that anything appeared. On a
 * viewport tall enough to show everything there is nothing to scroll, so this
 * no-ops rather than nudging a settled layout.
 */
export function useAutoScrollToBottom(
  scrollRef: React.RefObject<HTMLElement | null>,
  isArmed: boolean
) {
  const hasScrolled = useRef(false);

  useEffect(() => {
    // Re-arms if the user clears a mandatory field and fills it again — the
    // reveal happens a second time, so the scroll has to as well.
    if (!isArmed) {
      hasScrolled.current = false;
      return;
    }
    if (hasScrolled.current) return;
    hasScrolled.current = true;

    const timer = setTimeout(() => {
      const el = scrollRef.current;
      if (!el || el.scrollHeight <= el.clientHeight) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
    }, REVEAL_SETTLE_MS);

    return () => clearTimeout(timer);
  }, [isArmed, scrollRef]);
}
